
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import HeroSlider from '../../components/feature/HeroSlider';
import ReviewSlider from '../../components/feature/ReviewSlider';
import Button from '../../components/base/Button';

export default function Home() {
  const { t } = useTranslation();

  useEffect(() => {
    // Add structured data for homepage
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${import.meta.env.VITE_SITE_URL}/#webpage`,
      "url": import.meta.env.VITE_SITE_URL,
      "name": "Professional Painting Services in Atlanta - WF & Y Painting LLC",
      "description": "Transform your property with expert interior and exterior painting services. Quality workmanship and customer satisfaction guaranteed.",
      "isPartOf": {
        "@type": "WebSite",
        "@id": `${import.meta.env.VITE_SITE_URL}/#website`
      },
      "about": {
        "@type": "LocalBusiness",
        "@id": `${import.meta.env.VITE_SITE_URL}/#organization`
      },
      "mainEntity": {
        "@type": "LocalBusiness",
        "@id": `${import.meta.env.VITE_SITE_URL}/#organization`
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    {
      icon: 'ri-brush-fill',
      title: t('interiorPainting'),
      description: 'Transform your indoor spaces with professional interior painting services using premium paints.',
      image: 'https://public.readdy.ai/ai/img_res/cf3f60c75d05a94f8fbb64ee6b4b49a3.jpg'
    },
    {
      icon: 'ri-home-4-fill',
      title: t('exteriorPainting'),
      description: 'Protect and beautify your property exterior with weather-resistant, high-quality paint solutions.',
      image: 'https://readdy.ai/api/search-image?query=House%20exterior%20with%20fresh%20professional%20paint%20job%2C%20modern%20residential%20home%2C%20clean%20lines%2C%20attractive%20curb%20appeal%2C%20professional%20exterior%20painting%2C%20suburban%20neighborhood&width=400&height=300&seq=service2&orientation=landscape'
    },
    {
      icon: 'ri-hammer-fill',
      title: t('drywall'),
      description: 'Complete drywall repair and carpentry services to prepare surfaces for perfect paint application.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20drywall%20repair%20and%20carpentry%20work%2C%20construction%20tools%2C%20home%20renovation%2C%20wall%20preparation%20for%20painting%2C%20skilled%20craftsman%20working&width=400&height=300&seq=service3&orientation=landscape'
    },
    {
      icon: 'ri-drop-fill',
      title: t('pressureWashing'),
      description: 'Thorough pressure washing services to clean and prepare surfaces before painting.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20pressure%20washing%20service%20cleaning%20house%20exterior%2C%20high%20pressure%20water%20cleaning%2C%20home%20maintenance%2C%20surface%20preparation%20for%20painting&width=400&height=300&seq=service4&orientation=landscape'
    },
    {
      icon: 'ri-road-map-fill',
      title: t('epoxyFloor'),
      description: 'Durable epoxy floor coatings for garages, basements, and commercial spaces.',
      image: 'https://readdy.ai/api/search-image?query=Modern%20garage%20with%20glossy%20epoxy%20floor%20coating%2C%20professional%20floor%20finish%2C%20durable%20industrial%20flooring%2C%20clean%20organized%20garage%20space&width=400&height=300&seq=service5&orientation=landscape'
    },
    {
      icon: 'ri-leaf-fill',
      title: t('deckStaining'),
      description: 'Professional deck staining and restoration to protect and enhance your outdoor spaces.',
      image: 'https://readdy.ai/api/search-image?query=Beautiful%20wooden%20deck%20with%20fresh%20stain%20finish%2C%20outdoor%20living%20space%2C%20deck%20restoration%2C%20professional%20wood%20staining%2C%20backyard%20patio%20area&width=400&height=300&seq=service6&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <HeroSlider />

      {/* Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
              {t('servicesTitle')}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('servicesSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="h-48 bg-cover bg-center" style={{ backgroundImage: `url(${service.image})` }} />
                <div className="p-6">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${service.icon} text-white text-xl`}></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {service.description}
                  </p>
                  <Link to="/services">
                    <Button variant="outline" size="sm">
                      {t('learnMore')}
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-6">
                {t('aboutTitle')}
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                {t('aboutDescription')}
              </p>
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">15+</div>
                  <div className="text-gray-600">Years Experience</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">500+</div>
                  <div className="text-gray-600">Projects Completed</div>
                </div>
              </div>
              <Link to="/about">
                <Button>
                  {t('learnMore')}
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20painting%20team%20at%20work%2C%20experienced%20painters%20with%20equipment%2C%20quality%20craftsmanship%2C%20Atlanta%20painting%20company%2C%20skilled%20workers%2C%20professional%20service&width=600&height=500&seq=about1&orientation=landscape"
                alt="About WF & Y Painting"
                className="rounded-xl shadow-lg w-full h-96 object-cover object-top"
              />
              <div className="absolute -bottom-6 -right-6 bg-blue-600 text-white p-6 rounded-xl shadow-lg">
                <div className="flex items-center space-x-3">
                  <i className="ri-award-fill text-2xl"></i>
                  <div>
                    <div className="font-bold">Sherwin Williams</div>
                    <div className="text-sm">Premium Partner</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      <ReviewSlider />

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            {t('contactTitle')}
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            {t('contactSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button variant="secondary" size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
                <i className="ri-mail-fill"></i>
                {t('getQuote')}
              </Button>
            </Link>
            <a href="tel:770-875-6990">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-600">
                <i className="ri-phone-fill"></i>
                770-875-6990
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
